import { LocateFixed, X } from 'lucide-react';
import React, { useEffect, useRef } from "react";
import { Controller } from "react-hook-form";


function createStreetViewLink(lat, lng) {
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
}

const Step2LocationMap = ({
  register,
  control,
  errors,
  setValue,
  selectedLocation,
  onPlaceSelected,
  handleCancel,
  posts,
  setPosts,
  mapRef,
  postsMarkersRef,
  selectedMarkerRef,
}) => {
  const Autocomplete = ({ onPlaceSelected }) => {
    const inputRef = useRef(null);

    useEffect(() => {
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: { country: "us" },
        }
      );
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        onPlaceSelected(place);
      });
    }, [onPlaceSelected]);

    return (
      <div className="relative">
        <input
          type="text"
          ref={inputRef}
          className="block w-full pl-10 pr-4 py-2.5 border-input rounded-md focus:ring-2 focus:ring-ring focus:border-ring transition-all duration-200 text-sm placeholder-muted-foreground bg-background"
          placeholder="Search for a location..."
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <LocateFixed className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    );
  };

  // Map initialization and post management - preserve original logic
  useEffect(() => {
    if (window.google && mapRef.current) {
      if (!mapRef.current.map) {
        mapRef.current.map = new window.google.maps.Map(mapRef.current, {
          center: { lat: 42.3662986, lng: -71.06216222263835 },
          zoom: 14,
          tilt: 0,
          gestureHandling: "greedy",
          mapTypeControl: true,
        });
      }

      const map = mapRef.current.map;

      // Create the floating input div (hidden by default)
      const inputDiv = document.createElement("div");
      inputDiv.style.position = "absolute";
      inputDiv.style.zIndex = "1000";
      inputDiv.style.display = "none";
      inputDiv.innerHTML = `
        <div style="background-color: white; padding: 15px; border-radius: 8px; box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.2); width: 250px; font-family: Arial, sans-serif; font-size: 12px;">
          <div style="font-weight: bold; margin-bottom: 10px;">Add Post</div>
          <input type="text" id="markerNote" placeholder="Enter post name" style="width: 100%; padding: 8px; border-radius: 5px; border: 1px solid #ccc; font-size: 12px;" />
          <div style="margin-top: 10px; display: flex; justify-content: flex-end; gap: 10px;">
            <button id="closeMarkerNote" style="background-color: #f0f0f0; color: #555; border: none; padding: 6px 12px; border-radius: 5px; cursor: pointer; font-size: 12px;">
              Cancel
            </button>
            <button id="saveMarkerNote" style="background-color: #DB2677; color: white; border: none; padding: 6px 12px; border-radius: 5px; cursor: pointer; font-size: 12px;">
              Save
            </button>
          </div>
        </div>
      `;
      document.body.appendChild(inputDiv);

      // Add the listener for the right-click (contextmenu) event
      const addMarkerOnContextmenu = map.addListener("contextmenu", (event) => {
        event.domEvent.preventDefault();

        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const location = { lat, lng };

        // Show the input div at the clicked position
        inputDiv.style.display = "block";
        inputDiv.style.left = `${event.pixel.x}px`;
        inputDiv.style.top = `${event.pixel.y}px`;

        // Focus on the input field
        const input = document.getElementById("markerNote");
        input.focus();

        // Save marker note
        document.getElementById("saveMarkerNote").onclick = function () {
          const note = input.value;
          if (note) {
            setPosts((prevPosts) => [
              ...prevPosts,
              { position: location, note },
            ]);

            // Add marker to the map
            const marker = new window.google.maps.Marker({
              position: location,
              map,
              title: "Post Marker",
            });

            postsMarkersRef.current.push(marker);

            const infoWindowContent = `
              <div style="padding: 10px; font-family: Arial, sans-serif; background-color: #fff; border-radius: 8px; box-shadow: 0px 0px 10px rgba(0,0,0,0.1);">
                <strong>Post:</strong> <br/> 
                <span style="color: #007bff;">${note}</span>
              </div>
            `;
            const infoWindow = new window.google.maps.InfoWindow({
              content: infoWindowContent,
            });

            marker.addListener("click", () => {
              infoWindow.open(map, marker);
            });
          }

          // Hide and reset the input box
          inputDiv.style.display = "none";
          input.value = "";
        };

        // Add event listener for the close button
        document.getElementById("closeMarkerNote").onclick = function () {
          inputDiv.style.display = "none";
          input.value = "";
        };
      });

      // Handle the case for clearing posts and other map interactions
      if (selectedLocation) {
        const { latitude, longitude } = selectedLocation;
        map.setCenter({ lat: latitude, lng: longitude });

        const marker = new window.google.maps.Marker({
          position: { lat: latitude, lng: longitude },
          map,
          title: "Selected Location",
          icon: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
        });

        selectedMarkerRef.current = marker;

        const infoWindow = new window.google.maps.InfoWindow({
          content: `<div><strong>Selected Location</strong></div>`,
        });

        marker.addListener("click", () => {
          infoWindow.open(map, marker);
        });
      }

      posts.forEach((post) => {
        const marker = new window.google.maps.Marker({
          position: post.position,
          map,
          title: "Post",
        });

        postsMarkersRef.current.push(marker);

        const infoWindowContent = `
          <div style="padding: 10px; font-family: Arial, sans-serif; background-color: #fff; border-radius: 8px; box-shadow: 0px 0px 10px rgba(0,0,0,0.1);">
            <strong>Post:</strong> <br/> 
            <span style="color: #007bff;">${post.note}</span>
          </div>
        `;
        const infoWindow = new window.google.maps.InfoWindow({
          content: infoWindowContent,
        });

        marker.addListener("click", () => {
          infoWindow.open(map, marker);
        });
      });

      // Clean up the listener when the component is unmounted
      return () => {
        window.google.maps.event.removeListener(addMarkerOnContextmenu);
        if (document.body.contains(inputDiv)) {
          document.body.removeChild(inputDiv);
        }
      };
    }
  }, [selectedLocation, posts, setPosts, mapRef, postsMarkersRef, selectedMarkerRef]);

  return (
    <div className="space-y-4">
      {/* Location Search */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Job Location *
        </label>
        {selectedLocation ? (
          <div className="flex items-center justify-between p-2 bg-brand-blue-light/10 border border-brand-blue-light/30 rounded-md">
            <span className="text-xs text-brand-blue-dark flex-1 font-medium">
              📍 {selectedLocation.formatted_address}
            </span>
            <button
              type="button"
              onClick={handleCancel}
              className="ml-2 text-destructive hover:text-destructive/80 transition-colors duration-200"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <Controller
            name="location"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                onPlaceSelected={(place) => {
                  field.onChange(place.formatted_address);
                  onPlaceSelected(place);
                }}
              />
            )}
          />
        )}
        {errors.location && (
          <p className="text-destructive text-xs mt-1">{errors.location.message}</p>
        )}
      </div>

      {/* Posts Section */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Posts
        </label>
        <div className="min-h-[50px] p-2 border border-border rounded-md bg-white/95 backdrop-blur-sm shadow-sm">
          {posts.length < 1 ? (
            <div className="text-center text-muted-foreground py-2">
              <svg
                className="mx-auto h-6 w-6 text-muted-foreground mb-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <p className="text-xs font-medium">Right-click on the map to add post locations</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {posts.map((post, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-brand-pink text-white"
                >
                  <a
                    href={createStreetViewLink(
                      post.position.lat,
                      post.position.lng
                    )}
                    target="_blank"
                    rel="noreferrer"
                    className="text-white hover:text-gray-200"
                  >
                    {post.note}
                  </a>
                </span>
              ))}
            </div>
          )}
        </div>
        {posts.length >= 1 && (
          <p className="text-xs text-muted-foreground mt-1">
            💡 Remove selected location to clear all posts
          </p>
        )}
      </div>

      {/* Map Container */}
      <div className="border border-border rounded-md overflow-hidden shadow-sm">
        <div
          ref={mapRef}
          className="w-full h-64"
          style={{ minHeight: "256px" }}
        />
      </div>

      {/* Instructions */}
      <div className="bg-brand-blue-light/10 border border-brand-blue-light/30 rounded-md p-3">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-4 w-4 text-brand-blue-dark"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-2">
            <h3 className="text-xs font-semibold text-brand-blue-dark">
              How to use the map:
            </h3>
            <div className="mt-1 text-xs text-brand-blue-dark/80">
              <ul className="list-disc list-inside space-y-0.5">
                <li>Search for a location using the search box above</li>
                <li>Right-click on the map to add post markers</li>
                <li>Click on markers to view details</li>
                <li>Click the X button to clear the selected location and all posts</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2LocationMap;
